import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileDown, Presentation as PresentationIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const PPTX_ROLES = ["ADMIN", "DIRECTION_SECURITE"];

export default function Rapports() {
  const { user } = useAuth();
  const [campagnes, setCampagnes] = useState([]);
  const [audits, setAudits] = useState([]);
  const [campagneId, setCampagneId] = useState("");

  useEffect(() => {
    api.get("/campagnes").then((res) => {
      setCampagnes(res.data);
      const archived = res.data.find((c) => c.statut === "ARCHIVE") || res.data.find((c) => c.annee === "2024");
      const cross = res.data.find((c) => c.type_campagne === "CROSS_ASSESSMENT");
      setCampagneId((archived || cross)?.campagne_id || res.data[0]?.campagne_id || "");
    });
    api.get("/audits").then((res) => setAudits(res.data));
  }, []);

  const filtered = audits
    .filter((a) => a.campagne_id === campagneId)
    .sort((a, b) => (a.site_nom || "").localeCompare(b.site_nom || ""));

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5" data-testid="rapports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Rapports PDF</h1>
        <Select value={campagneId} onValueChange={setCampagneId}>
          <SelectTrigger className="w-56" data-testid="rapports-campagne-select"><SelectValue placeholder="Campagne" /></SelectTrigger>
          <SelectContent>{campagnes.map((c) => <SelectItem key={c.campagne_id} value={c.campagne_id}>{c.nom}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {user?.role === "ADMIN" && (
        <Button
          className="gap-2 bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
          data-testid="rapports-national-download-button"
          disabled={!campagneId}
          onClick={() => window.open(`${API}/reports/national?campagne_id=${campagneId}`, "_blank")}
        >
          <FileDown className="h-4 w-4" /> Télécharger le rapport national (tous les sites)
        </Button>
      )}
      {PPTX_ROLES.includes(user?.role) && (
        <Button
          variant="outline"
          className="gap-2 w-full sm:w-auto"
          data-testid="rapports-national-pptx-button"
          disabled={!campagneId}
          onClick={() => window.open(`${API}/reports/national/pptx?campagne_id=${campagneId}`, "_blank")}
        >
          <PresentationIcon className="h-4 w-4" /> Synthèse nationale (PPTX)
        </Button>
      )}

      <Card data-testid="rapports-sites-card">
        <CardHeader><CardTitle>Rapports par site</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {filtered.length ? filtered.map((a) => (
            <div key={a.audit_id} className="flex items-center justify-between gap-3 border-b py-2 last:border-0" data-testid={`rapports-row-${a.audit_id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{a.site_nom}</div>
                <div className="text-xs text-muted-foreground">{a.site_categorie} · Progression {a.progression}%</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline" size="sm" className="gap-1.5"
                  data-testid={`rapports-download-${a.audit_id}`}
                  onClick={() => window.open(`${API}/reports/site/${a.audit_id}`, "_blank")}
                >
                  <FileDown className="h-3.5 w-3.5" /> PDF
                </Button>
                {PPTX_ROLES.includes(user?.role) && (
                  <Button
                    variant="outline" size="sm" className="gap-1.5"
                    data-testid={`rapports-pptx-${a.audit_id}`}
                    onClick={() => window.open(`${API}/reports/site/${a.audit_id}/pptx`, "_blank")}
                  >
                    <PresentationIcon className="h-3.5 w-3.5" /> PPTX
                  </Button>
                )}
              </div>
            </div>
          )) : <p className="text-sm text-muted-foreground">Aucun audit pour cette campagne.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
